import {
    AlertTriangleIcon,
    CheckCircle2Icon,
    ChevronDownIcon,
    HeartIcon,
    InfoIcon,
    MessageSquareIcon,
    MoreVerticalIcon,
    RotateCcwIcon,
    StickyNoteIcon,
    Trash2Icon,
} from "lucide-react";
import { Applicant, ApplicantJobInsight, InterviewInsightItem } from "@type/applicant";
import {
    convertCapitalizeWords,
    convertToLowerSnakeCase,
    getApplicantStatusBadge,
} from "@util/string";
import { getMatchBadge } from "@util/job";
import { dateExpiredDate, dateExpiredDateInDay, parseFormat, parseFormatRaw } from "@util/date";
import { useEffect, useState } from "react";
import { applicantUpdateStatus } from "@service/job";
import {
    advanceApplicationFlow,
    isClientTimeout,
    FLOW_TIMEOUT_TITLE,
    FLOW_TIMEOUT_HINT,
    resolveFlowGate,
    FlowGateResolution,
} from "@service/hiring-flow";
import { getCookie } from "@util/cookie";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { ShortlistedCandidateItem } from "@type/candidate";
import { Job } from "@type/job";
import { Tooltip } from "./tooltip";
import { Badge } from "./badge";
import { Button } from "./button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./dialog";
import { cn } from "@util/tailwind";
import { useMaintenanceGate } from "@context/maintenance";
import { Checkbox } from "./checkbox";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./dropdown-menu";
import ShortlistToInviteDialog from "@container/dialog/dialog-shortlist-invite";
import DialogScoreBreakdown from "@container/dialog/dialog-score-breakdown";
import DropdownApplicantStatus from "./dropdown-applicant-status";
import ReviewScheduleManagerDialog from "./review-schedule-manager-dialog";
import InternalOfferNoteDialog from "@container/dialog/dialog-internal-offer-note";

interface Props {
    job: Job | null
    applicant: Applicant
    activeTab: string
    shortlistedCount: number
    applicantInsight?: ApplicantJobInsight
    isLoadingInsight?: boolean
    onSelectedApplicant: (applicantID: string, selected: boolean) => void
    onShowRejectReason: () => void
    onViewProfile: () => void
    onViewReview?: () => void
    commentCount?: number
    onUpdateScoreSimilarity: (applicantID: string, score: number) => void;
    onApplicantStatusChange: (applicantID: string, status: string) => void;
    onDeleteCandidate?: (applicant: Applicant) => void;
    // Hiring-flow board columns (#3073/#3075) — rendered only for custom-flow jobs.
    showFlowColumns?: boolean;
    // Called after a flow action (advance / gate resolve) so the parent reloads the board.
    onFlowAction?: () => void;
    // #3186: reject a gate-flagged candidate (Flagged tab) via the existing reject flow.
    onRejectFlagged?: (applicant: Applicant) => void;
}

/**
 * STATUS-chip tooltip copy for the "Screening In Progress" (pending_assessment)
 * tab. Returns "" when a status has nothing to explain, so the caller renders a
 * bare Badge instead of an empty black tooltip box (#3133). Keyed by status so a
 * new status added to this stage degrades to "no tooltip", never a blank box.
 *
 * `getFunnelStageByStatus` maps 8 statuses into this stage; `hired` and the
 * `rejected` family map to the Closed stage and can never appear here, so they
 * carry no copy (the old inline chain had dead arms for them; the rejection note
 * is still reachable via the reject_notes popover below).
 */
function getScreeningStatusTooltip(statusKey: string, updatedAt: string, expiryDays: number): string {
    switch (statusKey) {
        case "link_expired":
            return `Screening link expired on ${dateExpiredDate(updatedAt, expiryDays) || "Unknown date"}.`;
        case "pending_assessment":
        case "form_incomplete": {
            const daysLeft = dateExpiredDateInDay(updatedAt, expiryDays);
            return daysLeft === "0"
                ? "Screening link is expiring today."
                : `Screening link is expiring in ${daysLeft || "Unknown date"} days.`;
        }
        case "partial_interview":
            return `Interview was partially completed on ${parseFormat(updatedAt, "D MMMM YYYY") || "Unknown date"}.`;
        case "aborted_interview":
            return `Interview was aborted on ${parseFormat(updatedAt, "D MMMM YYYY") || "Unknown date"}.`;
        // Hiring-flow statuses (#3074) — previously unmapped → empty tooltip box.
        case "interview_incomplete":
            return "The candidate started the AI Interview but did not complete it.";
        case "calling_in_progress":
            return "Maya is calling the candidate right now — the status returns to normal when the call ends.";
        case "knockout_flagged":
            return "An answer gate failed. Keep the candidate in the job, reroute them, or archive.";
        case "deadline_missed":
            return "A deadline gate expired. Extend the deadline, defer to the next intake, or archive.";
        default:
            return "";
    }
}

export default function ApplicantItem({ job, applicant, activeTab, shortlistedCount, commentCount = 0, applicantInsight, isLoadingInsight, onSelectedApplicant, onApplicantStatusChange, onViewProfile, onViewReview, onShowRejectReason, onUpdateScoreSimilarity, onDeleteCandidate, showFlowColumns = false, onFlowAction, onRejectFlagged }: Props) {
    const maintenance = useMaintenanceGate();
    const snackbar = useSnackbar();
    const [, currentUserInfo] = useUserProfileContext();
    const { isWorkspaceRestricted } = useSubscription();
    const [shortlistDialogOpen, setShortlistDialogOpen] = useState<boolean>(false);
    const [matchPercentage, setMatchPercentage] = useState<number | string>(applicant.similarity_score === "-" ? "-" : Number(applicant.similarity_score));
    const [isShortlisted, setIsShortlisted] = useState<boolean>(false);
    const [isOpenDialogScoreBreakdown, setIsOpenDialogScoreBreakdown] = useState<boolean>(false);
    const [isReviewScheduleManagerDialogOpen, setIsReviewScheduleManagerDialogOpen] = useState<boolean>(false);
    const [isInternalOfferNoteDialogOpen, setIsInternalOfferNoteDialogOpen] = useState<boolean>(false);
    const [collapsedInsights, setCollapsedInsights] = useState<boolean>(true);

    const isLoadingGenerateInsight = isLoadingInsight ?? false;

    useEffect(() => {
        if (applicant.similarity_score === "-") {
            // The parent (employer-applicants-list) batch-calculates similarity scores
            // for all unscored applicants in a single request and feeds the result back
            // via the `applicant.similarity_score` prop, so this row must not call the
            // similarity API itself (avoids an N+1 fan-out). Keep the spinner until the
            // batch result arrives and updates the prop.
            setMatchPercentage("-");
        } else {
            setMatchPercentage(Number(applicant.similarity_score));
            onUpdateScoreSimilarity(applicant.applicant_id, Number(applicant.similarity_score));
        }
    }, [applicant.similarity_score])

    useEffect(() => {
        if (applicant && currentUserInfo) {
            const shortlistedCandidates = getCookie(`shortlisted_candidate:${currentUserInfo?.id}`);
            if (shortlistedCandidates) {
                const exists = Boolean((shortlistedCandidates[applicant.applicant_id as keyof ShortlistedCandidateItem] || []).find((item: ShortlistedCandidateItem) => item.job_id === job?.id));
                setIsShortlisted(exists);
            } else {
                setIsShortlisted(false);
            }
        }
    }, [applicant, currentUserInfo])

    const handleApplicantStatusChange = (newStatus: string) => {
        if (newStatus === applicant.status) {
            return;
        }

        if (newStatus === "review_scheduled") {
            setIsReviewScheduleManagerDialogOpen(true);
            return;
        } else if (newStatus === "offer_resent") {
            setIsInternalOfferNoteDialogOpen(true);
            return;
        }


        applicantUpdateStatus(job?.id || "", {
            user_id: applicant.applicant_id,
            status: convertCapitalizeWords(newStatus),
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${getApplicantStatusBadge(newStatus).label}.`);
                onApplicantStatusChange(applicant.applicant_id, newStatus);
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status. Please try again.");
        })
    }

    // Hiring-flow row actions (#3074/#3075): Advance a checkpoint / mark a human
    // interview done, and resolve gate holds (Knockout Flagged / Deadline
    // Missed). `applicant.id` is the application id the flow endpoints take.
    const [isFlowActionLoading, setFlowActionLoading] = useState<boolean>(false);

    const handleFlowAdvance = () => {
        setFlowActionLoading(true);
        advanceApplicationFlow(applicant.id)
            .then(() => {
                snackbar.showSuccess("Candidate advanced to the next step.");
                onFlowAction?.();
            })
            .catch((error) => {
                catchErrorLog(`failed to advance flow: ${error}`);
                if (isClientTimeout(error)) {
                    // Idempotent on the BE — the advance may well have landed.
                    // Refresh instead of sending HR to retry blind.
                    snackbar.showError(FLOW_TIMEOUT_TITLE, FLOW_TIMEOUT_HINT);
                    onFlowAction?.();
                    return;
                }
                snackbar.showError("Failed to advance the candidate. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    // #3297 (BE ourteam-backend#4031): every step completed but the terminal
    // transition never fired — the row is stuck in Screening In Progress. The
    // ordinary flow/advance call performs the finalize; idempotent on the BE.
    const handleMoveToReview = () => {
        setFlowActionLoading(true);
        advanceApplicationFlow(applicant.id)
            .then(() => {
                snackbar.showSuccess("Candidate moved to Ready for Review.");
                onFlowAction?.();
            })
            .catch((error) => {
                const beMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
                catchErrorLog(`failed to finalize flow: ${error}`);
                if (isClientTimeout(error)) {
                    snackbar.showError(FLOW_TIMEOUT_TITLE, FLOW_TIMEOUT_HINT);
                    onFlowAction?.();
                    return;
                }
                snackbar.showError(beMessage || "Failed to move the candidate. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    // #3250 (BE ourteam-backend#3992): approve a pending call-retake request.
    // The ordinary flow/advance call doubles as the approval; idempotent on the
    // BE, so a double submit cannot reopen the interview twice.
    const handleApproveRetake = () => {
        setFlowActionLoading(true);
        advanceApplicationFlow(applicant.id)
            .then(() => {
                snackbar.showSuccess("Retake approved — the interview is reopened for the candidate.");
                onFlowAction?.();
            })
            .catch((error) => {
                catchErrorLog(`failed to approve retake: ${error}`);
                if (isClientTimeout(error)) {
                    snackbar.showError(FLOW_TIMEOUT_TITLE, FLOW_TIMEOUT_HINT);
                    onFlowAction?.();
                    return;
                }
                snackbar.showError("Failed to approve the retake. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    const handleFlowResolve = (resolution: FlowGateResolution, successMessage: string) => {
        setFlowActionLoading(true);
        resolveFlowGate(applicant.id, resolution)
            .then(() => {
                snackbar.showSuccess(successMessage);
                onFlowAction?.();
            })
            .catch((error) => {
                catchErrorLog(`failed to resolve flow gate: ${error}`);
                snackbar.showError("Failed to resolve the flag. Please try again.");
            })
            .finally(() => setFlowActionLoading(false));
    };

    // Keep-in-job / Archive are one-click gate resolutions with real
    // consequences (waive the flag / move to Closed) — interpose a confirm
    // dialog so a stray click on the compact row links can't fire them.
    const [confirmResolveAction, setConfirmResolveAction] = useState<"keep" | "archive" | null>(null);

    const handleConfirmResolve = () => {
        if (confirmResolveAction === "keep") {
            handleFlowResolve({ action: "keep" }, "Flag waived — the flow resumes.");
        } else if (confirmResolveAction === "archive") {
            handleFlowResolve({ action: "archive" }, "Candidate archived (restorable).");
        }
        setConfirmResolveAction(null);
    };

    const handleMarkReSendOffer = (applicant_ids: string[], note: string) => {
        applicantUpdateStatus(job?.id || "", {
            user_ids: applicant_ids,
            status: convertCapitalizeWords("offer_resent"),
            offer_notes: note,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${convertCapitalizeWords("offer_resent")}.`);
                onApplicantStatusChange(applicant.applicant_id, "offer_resent");
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status to offer sent. Please try again.");
        })
    }

    const applicantStatus = getApplicantStatusBadge(convertToLowerSnakeCase(applicant.status))
    // #3133: resolve the STATUS-chip tooltip copy up front, so we can skip the
    // <Tooltip> entirely for statuses with no copy (no empty black box).
    const statusTooltipCopy = getScreeningStatusTooltip(
        convertToLowerSnakeCase(applicant.status),
        applicant?.updated_at || "",
        job?.application_expiry_days || 7,
    )

    // #3201: this candidate is pinned to an OLDER hiring-flow version than the job's
    // current one (HR edited a step after they progressed). Surface it so HR sees why
    // their steps differ from the current flow. Uses the candidate's pinned version vs
    // the job's current version — both already on the payload; no badge otherwise.
    const olderFlowVersion =
        showFlowColumns &&
        applicant.flow_version != null &&
        job?.flow_version != null &&
        applicant.flow_version < job.flow_version
            ? applicant.flow_version
            : null;

    // #3297: all steps done, no current step, not gate-held — yet still in
    // Screening In Progress (missed finalize transition, BE ourteam-backend#4031).
    const flowStuckCompleted =
        !applicant.paused_at &&
        !applicant.current_step &&
        (applicant.flow_progress?.total ?? 0) > 0 &&
        applicant.flow_progress?.completed === applicant.flow_progress?.total;

    // #3250: pending call-retake notice + Approve, rendered in the flow columns.
    // A candidate who asked has finished the interview, so they usually sit in
    // Ready for Review — not flagged. While pending, the regular Advance buttons
    // hide: the same endpoint would approve the retake, and two differently-
    // labeled buttons doing one thing reads wrong.
    const retakeNotice = applicant.retake_request ? (
        <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700">
                <RotateCcwIcon className="w-3.5 h-3.5 flex-shrink-0" />
                Retake requested — {applicant.retake_request.step_label}
            </p>
            <p className="text-xs text-gray-500">Asked {parseFormatRaw(applicant.retake_request.requested_at, "D MMM YYYY h:mm A")}</p>
            {applicant.retake_request.reason && (
                /* Popover (same pattern as flag_reason): the note can be long —
                   truncated trigger, click to read it all. */
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="block max-w-[220px] truncate text-left text-xs text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700"
                            onClick={(e) => e.stopPropagation()}
                        >
                            “{applicant.retake_request.reason}”
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Candidate's note</p>
                        <p className="text-sm leading-snug text-gray-700 dark:text-gray-200">{applicant.retake_request.reason}</p>
                    </PopoverContent>
                </Popover>
            )}
            <button
                disabled={isFlowActionLoading}
                className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-800 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-50"
                onClick={handleApproveRetake}
            >
                {isFlowActionLoading ? "Approving…" : "Approve retake"}
            </button>
        </div>
    ) : null;

    return (
        <>
            <tr key={applicant.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer" onClick={onViewProfile}>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div
                        className="flex items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            checked={applicant.is_selected || false}
                            onCheckedChange={(checked) =>
                                onSelectedApplicant(applicant.applicant_id, Boolean(checked))
                            }
                            aria-label={`Select ${applicant.applicant_name}`}
                        />
                    </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div className="flex flex-col items-center gap-0.5">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {/* CV-less flow candidates (auto-start / CV-optional entry,
                                #3077/#3090) have no cv_data — fall back to the name. */}
                            {applicant.applicant_name || applicant.cv_data?.CV_data?.name || "—"}
                        </div>
                        {/* #3201: older-flow-version marker — HR edited the flow after this
                            candidate progressed, so their steps follow an earlier version. */}
                        {olderFlowVersion !== null && (
                            <Tooltip content={<p className="max-w-xs">This candidate ran an earlier version of the hiring flow (v{olderFlowVersion}); the current flow is v{job?.flow_version}. Their steps and submitted data follow that earlier version.</p>}>
                                <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                                    Flow v{olderFlowVersion}
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-gray-500 dark:text-gray-400">
                    {applicant.cv_data?.CV_data?.location || <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                    {parseFormatRaw(applicant.created_at, "D MMM YYYY h:mm A")}
                </td>
                {activeTab === "pending_assessment" && (
                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                        {parseFormat(applicant.pending_assessment_at ?? "", "D MMM YYYY h:mm A")}
                    </td>
                )}
                {activeTab === "to_review" && (
                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                        {applicant.pending_review_at ? parseFormat(applicant.pending_review_at, "D MMM YYYY h:mm A") : "-"}
                    </td>
                )}
                {activeTab === "offers" && (
                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                        {applicant.offer_sent_at ? parseFormat(applicant.offer_sent_at, "D MMM YYYY h:mm A") : "-"}
                    </td>
                )}
                {activeTab === "closed" && (
                    <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                        {applicant.updated_at ? parseFormat(applicant.updated_at, "D MMM YYYY h:mm A") : "-"}
                    </td>
                )}
                {/* Hiring-flow columns (#3073) — Assessment In Progress scan level:
                    monotonic progress + current step (with pause/flag context). */}
                {showFlowColumns && activeTab === "pending_assessment" && (
                    <>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                            {applicant.flow_progress?.total ? (
                                <div className="inline-flex flex-col items-center gap-1">
                                    <div className="inline-flex items-center gap-2">
                                        <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${applicant.flow_progress.percent}%` }} />
                                        </div>
                                        <span className="text-xs text-gray-600 tabular-nums">
                                            {applicant.flow_progress.completed} / {applicant.flow_progress.total}
                                        </span>
                                    </div>
                                    <span className="text-xs text-gray-400">
                                        {applicant.channel === "whatsapp_first" ? "WhatsApp" : "Web"}
                                    </span>
                                </div>
                            ) : (
                                <span className="text-gray-400">—</span>
                            )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                            {applicant.paused_at ? (
                                <div className="space-y-1">
                                    <div className="text-sm font-medium text-amber-700 flex items-center gap-1.5">
                                        <AlertTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                        {applicant.paused_at.label}
                                    </div>
                                    {applicant.flag_reason && (
                                        /* Popover (not a native title tooltip) — with multiple knockout
                                           conditions (#3236) the reason gets long; click to read it all. */
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="block max-w-[220px] truncate text-left text-xs text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {applicant.flag_reason}
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent align="start" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
                                                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Flag reason</p>
                                                <ul className="space-y-1.5">
                                                    {applicant.flag_reason.split(/;\s+/).map((part, idx) => (
                                                        <li key={idx} className="text-sm leading-snug text-gray-700 dark:text-gray-200">{part}</li>
                                                    ))}
                                                </ul>
                                            </PopoverContent>
                                        </Popover>
                                    )}
                                    {/* Gate resolutions (#3074): flag & pause only — HR decides. */}
                                    <div className="flex items-center gap-2">
                                        {convertToLowerSnakeCase(applicant.status) === "knockout_flagged" ? (
                                            <>
                                                <button disabled={isFlowActionLoading} className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50" onClick={() => setConfirmResolveAction("keep")}>Keep in job</button>
                                                {/* Reroute (#3086, BE #3662): the held gate carries a
                                                    suggested job — one click copies the application there. */}
                                                {applicant.paused_at.suggest_job?.id && (
                                                    <button
                                                        disabled={isFlowActionLoading}
                                                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                        onClick={() => handleFlowResolve({ action: "reroute" }, `Candidate rerouted to "${applicant.paused_at?.suggest_job?.title || "the suggested job"}".`)}
                                                    >
                                                        Reroute to {applicant.paused_at.suggest_job.title || "suggested job"}
                                                    </button>
                                                )}
                                                <button disabled={isFlowActionLoading} className="text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50" onClick={() => setConfirmResolveAction("archive")}>Archive</button>
                                                {onRejectFlagged && (
                                                    <button disabled={isFlowActionLoading} className="text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50" onClick={() => onRejectFlagged(applicant)}>Reject</button>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <button disabled={isFlowActionLoading} className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50" onClick={() => handleFlowResolve({ action: "extend", extend_due: "+48h" }, "Deadline extended — the flow resumes.")}>Extend</button>
                                                <button disabled={isFlowActionLoading} className="text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50" onClick={() => handleFlowResolve({ action: "next_intake" }, "Deferred to the next intake (archived, restorable).")}>Next intake</button>
                                                <button disabled={isFlowActionLoading} className="text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50" onClick={() => setConfirmResolveAction("archive")}>Archive</button>
                                                {onRejectFlagged && (
                                                    <button disabled={isFlowActionLoading} className="text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50" onClick={() => onRejectFlagged(applicant)}>Reject</button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : applicant.current_step ? (
                                <div>
                                    <p className="text-sm text-gray-900">{applicant.current_step.label}</p>
                                    {applicant.delivery_state && ["failed", "undelivered"].includes(applicant.delivery_state) && (
                                        <p className="text-xs text-red-600">WhatsApp send {applicant.delivery_state}</p>
                                    )}
                                    {/* Maya escalation (#3091): Maya handed the chat off — HR reply needed. */}
                                    {applicant.maya_escalation?.needs_reply && (
                                        <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                                            <MessageSquareIcon className="w-3 h-3" />
                                            Maya escalated — reply needed
                                        </p>
                                    )}
                                    {retakeNotice && <div className="mt-1.5">{retakeNotice}</div>}
                                </div>
                            ) : flowStuckCompleted ? (
                                <button
                                    disabled={isFlowActionLoading}
                                    className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-800 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-50"
                                    onClick={handleMoveToReview}
                                >
                                    {isFlowActionLoading ? "Moving…" : "Move to Ready for Review"}
                                </button>
                            ) : (
                                <span className="text-gray-400">—</span>
                            )}
                        </td>
                    </>
                )}
                {/* Ready-for-Review checkpoint columns + Advance action (#3075). */}
                {/* "Just Completed" cell removed; "Next Step" cell moved to after Insights (below). */}
                <td className="px-4 py-4 whitespace-nowrap text-center" onClick={(e) => { if (activeTab === "applied" || activeTab === "to_review" || activeTab === "offers") e.stopPropagation(); }}>
                    {(activeTab === "to_review" || activeTab === "offers" || activeTab === "applied") ? (
                        <div className="inline-flex flex-col items-center gap-1">
                            <DropdownApplicantStatus
                                currentStatus={convertToLowerSnakeCase(applicantStatus.label)}
                                onStatusChange={handleApplicantStatusChange}
                                isOffersTab={activeTab === "offers"}
                                isIntakeTab={activeTab === "applied"}
                                offerMetadata={applicant.offer_metadata}
                                offerNotes={applicant.offer_notes}
                                disabled={isWorkspaceRestricted}
                            />
                            {/* Applied entry impact (#3073): auto-start candidates stay
                                visible here only when the flow could NOT start — surface
                                the send failure so HR can retry / invite manually. */}
                            {showFlowColumns && activeTab === "applied" && applicant.delivery_state && ["failed", "undelivered"].includes(applicant.delivery_state) && (
                                <Tooltip side="top" content={<p>The WhatsApp invitation could not be delivered — resend the screening invitation or switch to a manual invite.</p>}>
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                                        <AlertTriangleIcon className="w-3 h-3" />
                                        Send {applicant.delivery_state}
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center gap-2">
                            {["pending_assessment"].includes(activeTab) && statusTooltipCopy ? (
                                <Tooltip
                                    side="top"
                                    content={<p>{statusTooltipCopy}</p>}
                                >
                                    <Badge
                                        className={cn("whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer", applicantStatus.className)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        style={{
                                            paddingTop: "4px",
                                            paddingBottom: "4px",
                                            paddingLeft: "8px",
                                            paddingRight: "8px",
                                            border:
                                                "0.909091px solid rgba(0, 0, 0, 0)",
                                            borderRadius: "1rem",
                                            boxShadow: "none",
                                        }}
                                    >
                                        {applicantStatus.label}
                                    </Badge>
                                </Tooltip>
                            ) : (
                                <Badge
                                    className={cn("whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer", applicantStatus.className)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    style={{
                                        paddingTop: "4px",
                                        paddingBottom: "4px",
                                        paddingLeft: "8px",
                                        paddingRight: "8px",
                                        border:
                                            "0.909091px solid rgba(0, 0, 0, 0)",
                                        borderRadius: "1rem",
                                        boxShadow: "none",
                                    }}
                                >
                                    {applicantStatus.label}
                                </Badge>
                            )}
                            {(!!applicant.reject_notes && ["rejected", "assessment_rejected", "cv_rejected", "ai_interview_rejected"].includes(convertToLowerSnakeCase(applicant.status))) && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center cursor-pointer p-1 rounded">
                                            <Tooltip
                                                side="top"
                                                content={(
                                                    <p>View internal rejection note</p>
                                                )}
                                            >
                                                <span className="text-sm text-blue-600 dark:text-blue-400 underline cursor-pointer hover:text-black dark:hover:text-black">
                                                    View
                                                </span>
                                            </Tooltip>
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-96"
                                        align="center"
                                    >
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="font-semibold text-base text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                                                    <StickyNoteIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                                    Internal Rejection Note
                                                </h4>
                                                <div className="space-y-1.5">
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            Rejected by:
                                                        </span>{" "}
                                                        {applicant.reject_metadata?.rejected_by || "N/A"}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            Date:
                                                        </span>{" "}
                                                        {applicant.reject_metadata?.rejected_at ? (
                                                            <>
                                                                {parseFormat(applicant.reject_metadata?.rejected_at || "", "D MMM YYYY")} at {parseFormat(applicant.reject_metadata?.rejected_at || "", "h:mm A")}
                                                            </>
                                                        ) : "N/A"}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            Rejected from:
                                                        </span>{" "}
                                                        {applicant.reject_metadata?.rejected_stage || "N/A"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                                    {applicant.reject_notes || "N/A"}
                                                </p>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    {matchPercentage === "-" ? (
                        <div className="flex justify-center items-center">
                            <svg className="animate-spin h-4 w-4 text-gray-800 mt-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : (
                        <Tooltip content={(
                            <p>View detailed scoring breakdown</p>
                        )}>
                            <div
                                className={`inline-block cursor-pointer hover:opacity-80 transition-opacity`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpenDialogScoreBreakdown(true)
                                }}
                            >
                                <Badge
                                    className={cn(getMatchBadge(
                                        matchPercentage as number,
                                    ),
                                        "inline-flex items-center gap-1.5"
                                    )}
                                    style={{
                                        paddingTop: "4px",
                                        paddingBottom: "4px",
                                        paddingLeft: "8px",
                                        paddingRight: "8px",
                                        border:
                                            "0.909091px solid rgba(0, 0, 0, 0)",
                                        borderRadius: "1rem",
                                        boxShadow: "none",
                                    }}
                                >
                                    {Number(matchPercentage) || 0}% match
                                    <InfoIcon className="w-3.5 h-3.5" />
                                </Badge>
                            </div>
                        </Tooltip>
                    )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                    {!isLoadingGenerateInsight ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCollapsedInsights(!collapsedInsights);
                                }}
                                className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                            >
                                <ChevronDownIcon
                                    className={cn(
                                        "w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform",
                                        collapsedInsights && "-rotate-90"
                                    )}
                                />
                            </button>
                            <div className="flex-1">
                                {!collapsedInsights && (
                                    <div className="space-y-1">
                                        {(applicantInsight?.insights || []).map((insight: InterviewInsightItem, index: number) => (
                                            <div key={index} className={cn("flex items-center gap-1.5 text-xs", {
                                                "text-green-700": insight.type === "positive",
                                                "text-amber-700": insight.type === "negative",
                                            })}>
                                                {insight.type === "positive" ? (
                                                    <CheckCircle2Icon className="w-3.5 h-3.5 flex-shrink-0" />
                                                ) : (
                                                    <AlertTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                )}
                                                <span>{insight.insight}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {collapsedInsights && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {(applicantInsight?.insights || []).filter((i) => i.type === "positive").length > (applicantInsight?.insights || []).filter((i) => i.type === "negative").length
                                            ? `${(applicantInsight?.insights || []).filter((i) => i.type === "positive").length} strengths`
                                            : (applicantInsight?.insights || []).filter((i) => i.type === "positive").length < (applicantInsight?.insights || []).filter((i) => i.type === "negative").length
                                                ? `${(applicantInsight?.insights || []).filter((i) => i.type === "negative").length} skill gaps`
                                                : (applicantInsight?.insights || []).filter((i) => i.type === "positive").length === 2 && (applicantInsight?.insights || []).filter((i) => i.type === "negative").length === 2
                                                    ? `${(applicantInsight?.insights || []).filter((i) => i.type === "positive").length} strengths and ${(applicantInsight?.insights || []).filter((i) => i.type === "negative").length} skill gaps`
                                                    : `${(applicantInsight?.insights || []).length} insights`}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center">
                            <svg className="animate-spin h-4 w-4 text-gray-800 mt-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    )}

                </td>
                {showFlowColumns && activeTab === "to_review" && (
                    <td className="px-4 py-4 whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                        <div className="space-y-1.5">
                            {retakeNotice}
                            {applicant.next_step ? (
                                <div>
                                    <p className="text-sm text-gray-900">{applicant.next_step.label}</p>
                                    {applicant.next_step.detail && (
                                        <p className="text-xs text-gray-500">{applicant.next_step.detail}</p>
                                    )}
                                </div>
                            ) : applicant.review_kind === "final" ? (
                                <p className="text-xs text-gray-500">Final hiring decision</p>
                            ) : null}
                            {!applicant.retake_request && applicant.review_kind === "checkpoint" && (
                                <button
                                    disabled={isFlowActionLoading}
                                    className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-800 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-50"
                                    onClick={handleFlowAdvance}
                                >
                                    {isFlowActionLoading ? "Advancing…" : "Advance to next step"}
                                </button>
                            )}
                            {!applicant.retake_request && applicant.review_kind === "human_interview" && (
                                <button
                                    disabled={isFlowActionLoading}
                                    className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-800 text-white text-xs font-medium px-3 py-1.5 disabled:opacity-50"
                                    onClick={handleFlowAdvance}
                                >
                                    {isFlowActionLoading ? "Advancing…" : "Mark done · Advance"}
                                </button>
                            )}
                        </div>
                    </td>
                )}
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    {/* Favourite and comment both write (#3573). */}
                    <div
                        className={`flex items-center justify-center ${maintenance.groupClass}`}
                        {...maintenance.groupProps}
                    >
                        <Tooltip content={(
                            <p>
                                {shortlistedCount > 0
                                    ? `${shortlistedCount} team member${shortlistedCount !== 1 ? "s have" : " has"} favourited this candidate`
                                    : "Favourite this candidate"}
                            </p>
                        )}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShortlistDialogOpen(true);
                                }}
                                className="flex items-center gap-1 p-1 rounded-full transition-colors group"
                            >
                                <HeartIcon className={cn("w-5 h-5 transition-colors", {
                                    "text-pink-500 fill-pink-500": isShortlisted,
                                    "text-gray-300 group-hover:text-pink-400 group-hover:fill-pink-400": !isShortlisted,
                                })} />
                                <span className={cn("text-sm font-medium text-pink-500 w-4 text-left leading-none", shortlistedCount === 0 && "invisible")}>
                                    {shortlistedCount}
                                </span>
                            </button>
                        </Tooltip>
                        {onViewReview && (
                            <Tooltip content={<p>Leave a comment</p>}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewReview();
                                    }}
                                    className="flex items-center gap-1 p-1 rounded-full transition-colors group"
                                    aria-label="Leave a comment"
                                >
                                    <MessageSquareIcon className={cn("w-5 h-5 transition-colors", commentCount > 0 ? "text-blue-500 fill-blue-500" : "text-gray-300 group-hover:text-blue-500 group-hover:fill-blue-500")} />
                                    <span className={cn("text-sm font-medium text-blue-500 w-4 text-left leading-none", commentCount === 0 && "invisible")}>
                                        {commentCount}
                                    </span>
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </td>
                <td className="px-2 py-4 whitespace-nowrap text-center">
                    {/* The row menu deletes a candidate — closed during a window. */}
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className={maintenance.groupClass}
                        {...maintenance.groupProps}
                    >
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 cursor-pointer">
                                    <MoreVerticalIcon className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                    onClick={() => onDeleteCandidate?.(applicant)}
                                    className="text-red-600 focus:text-red-600 cursor-pointer"
                                >
                                    <Trash2Icon className="w-4 h-4 mr-2" />
                                    <span className="text-sm">Delete candidate</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </td>
            </tr >

            {/* Confirm dialog for the row's Keep-in-job / Archive gate resolutions. */}
            <Dialog open={confirmResolveAction !== null} onOpenChange={(open) => { if (!open) setConfirmResolveAction(null); }}>
                <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>
                            {confirmResolveAction === "keep" ? "Keep candidate in job?" : "Archive candidate?"}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmResolveAction === "keep"
                                ? `The flag on ${applicant.applicant_name || applicant.cv_data?.CV_data?.name || "this candidate"} will be waived and their hiring flow resumes at the paused step.`
                                : `${applicant.applicant_name || applicant.cv_data?.CV_data?.name || "This candidate"} moves to the Closed stage. No notification is sent, and you can restore them later.`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setConfirmResolveAction(null)}>Cancel</Button>
                        <Button
                            disabled={isFlowActionLoading}
                            className={confirmResolveAction === "keep" ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
                            onClick={handleConfirmResolve}
                        >
                            {confirmResolveAction === "keep" ? "Keep in job" : "Archive"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ShortlistToInviteDialog
                candidate_id={applicant.applicant_id || ""}
                candidate_name={applicant.applicant_name || applicant.cv_data?.CV_data?.name || ""}
                isOpen={shortlistDialogOpen}
                selectedJob={job}
                onClose={() => {
                    setShortlistDialogOpen(false);
                }}
            />

            <DialogScoreBreakdown
                isOpen={isOpenDialogScoreBreakdown}
                onClose={() => setIsOpenDialogScoreBreakdown(false)}
                applicant={applicant}
                candidate_id={applicant.applicant_id}
                candidate_name={applicant.applicant_name || applicant.cv_data?.CV_data?.name || ""}
                match_score={matchPercentage as number}
                job_id={job?.id || ""}
                job_title={job?.job_title || ""}
                applied_date={applicant.created_at}
                assessment_ids={job?.assessment_ids}
            />

            <ReviewScheduleManagerDialog
                isOpen={isReviewScheduleManagerDialogOpen}
                job={job}
                applicants={[
                    applicant
                ]}
                onClose={() => setIsReviewScheduleManagerDialogOpen(false)}
                onReload={() => {
                    onApplicantStatusChange(applicant.applicant_id, "review_scheduled");
                }}
            />

            <InternalOfferNoteDialog
                isOpen={isInternalOfferNoteDialogOpen}
                applicants={[applicant]}
                onClose={() => setIsInternalOfferNoteDialogOpen(false)}
                onSubmit={(note: string) => {
                    handleMarkReSendOffer([applicant.applicant_id], note);
                    setIsInternalOfferNoteDialogOpen(false);
                }}
            />
        </>
    )
}